import { createFileRoute } from '@tanstack/react-router'
import * as React from "react";
import { API_ADMIN_BASE_URL } from "@/lib/api-client";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious,
} from "@/components/ui/pagination";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Search } from "lucide-react";

interface AuditLog {
  id: string;
  adminId: string;
  adminEmail: string;
  action: string;
  targetType: string;
  targetId: string;
  details: Record<string, unknown> | null;
  ipAddress: string | null;
  createdAt: string;
}

const fmtDate = (d: string) => new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });

function AuditLogsPage() {
  const [logs, setLogs] = React.useState<AuditLog[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [total, setTotal] = React.useState(0);
  const [actionFilter, setActionFilter] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [expandedId, setExpandedId] = React.useState<string | null>(null);
  const limit = 20;

  const fetchLogs = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", limit.toString());
      params.set("offset", ((page - 1) * limit).toString());
      if (actionFilter) params.set("action", actionFilter);
      const res = await fetch(`${API_ADMIN_BASE_URL}/api/admin/audit-logs?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      const json = await res.json();
      setLogs(json.data.logs);
      setTotal(json.data.total);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [actionFilter, page]);

  React.useEffect(() => { fetchLogs(); }, [fetchLogs]);
  React.useEffect(() => { document.title = "Audit Logs - Admin"; }, []);
  React.useEffect(() => { setPage(1); }, [actionFilter]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="bg-background h-full overflow-auto">
      <header>
        <div className="border-border flex h-12 shrink-0 items-center gap-2 border-b px-4 md:h-16 md:px-6">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem><BreadcrumbLink href="/dashboard">Dashboard</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbPage>Audit Logs</BreadcrumbPage></BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <div className="border-border border-b px-4 py-4 md:px-6 md:py-6">
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">Audit Logs</h1>
          <p className="text-muted-foreground text-sm">{total.toLocaleString()} log entries</p>
        </div>
      </header>
      <main className="flex-1 space-y-6 px-4 py-6 md:px-6">
        <div className="relative w-full md:w-[300px]">
          <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input placeholder="Filter by action (e.g. wallet.adjust)..." className="pl-8" value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} />
        </div>
        <div className="relative overflow-hidden rounded-lg border">
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Admin</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Target ID</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
                ) : logs.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No audit logs found</TableCell></TableRow>
                ) : logs.map((l) => (
                  <React.Fragment key={l.id}>
                    <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => setExpandedId(expandedId === l.id ? null : l.id)}>
                      <TableCell><span className="text-muted-foreground text-xs whitespace-nowrap">{fmtDate(l.createdAt)}</span></TableCell>
                      <TableCell className="text-sm">{l.adminEmail}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{l.action}</Badge></TableCell>
                      <TableCell><span className="text-sm">{l.targetType}</span></TableCell>
                      <TableCell><span className="font-mono text-xs">{l.targetId.slice(0, 12)}</span></TableCell>
                      <TableCell><span className="text-xs text-muted-foreground">{l.details ? "Click to expand" : "-"}</span></TableCell>
                    </TableRow>
                    {expandedId === l.id && l.details && (
                      <TableRow>
                        <TableCell colSpan={6} className="bg-muted/30">
                          <pre className="text-xs overflow-auto max-h-[200px] p-2">{JSON.stringify(l.details, null, 2)}</pre>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
        {totalPages > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem><PaginationPrevious onClick={() => setPage(Math.max(1, page - 1))} className={page === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"} /></PaginationItem>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pn: number; if (totalPages <= 5) pn = i + 1; else if (page <= 3) pn = i + 1; else if (page >= totalPages - 2) pn = totalPages - 4 + i; else pn = page - 2 + i;
                return (<PaginationItem key={pn}><PaginationLink onClick={() => setPage(pn)} isActive={page === pn} className="cursor-pointer">{pn}</PaginationLink></PaginationItem>);
              })}
              <PaginationItem><PaginationNext onClick={() => setPage(Math.min(totalPages, page + 1))} className={page === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"} /></PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </main>
    </div>
  );
}

export const Route = createFileRoute('/dashboard/audit-logs')({
  component: AuditLogsPage,
})
